<template>
  <div
    :class="['jpm-sticky-cta', { 'jpm-sticky-cta--visible': showSticky }]"
    :aria-hidden="!showSticky"
  >
    <UCard
      class="mx-2"
      :ui="{
        body: 'p-2 sm:p-4'
      }"
    >
      <div class="flex gap-2 sm:gap-4">
        <UButton
          v-for="link in page?.hero.links"
          :key="link.label"
          :label="link.label"
          :icon="link.icon"
          :trailing="link.trailing"
          :to="link.to"
          :target="link.target"
          size="xl"
          :variant="allowedVariant(link.variant)"
          :color="allowedColor(link.color)"
          class="flex-1 justify-center"
          :disabled="!showSticky"
          :aria-hidden="!showSticky"
          :aria-disabled="!showSticky"
          :tabindex="showSticky ? null : -1"
        />
      </div>
    </UCard>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

// Define a type for the page state
type PageHeroLink = {
  label: string
  icon?: string
  trailing?: boolean
  to?: string
  target?: string
  variant?: string
  color?: string
}
type PageState = {
  hero: {
    links: PageHeroLink[]
  }
}

const page = useState<PageState>('page')
const { isMobile } = useDevice()

// Keep the existing hero visibility composable for hero timing
const { isHeroInView } = useHeroVisibility('#page-hero', {
  root: null,
  // show CTA earlier when hero mostly scrolled out
  rootMargin: '0px 0px 80% 0px',
  threshold: isMobile ? 0.3 : 0.45
})

// Observe the get-started section so we can hide the CTA when that section is visible
const { isInView: isGetStartedInView } = useVisibility('#get-started', {
  root: null,
  // hide CTA as soon as the get-started section is approaching the viewport;
  // tweak this value if you want it to hide earlier/later
  rootMargin: '0px 0px -10% 0px',
  threshold: isMobile ? 0.545 : 0.3
})

const showSticky = computed(
  () => !isHeroInView.value && !isGetStartedInView.value
)

// Only allow valid variants for UButton
const allowedVariants = [
  'link',
  'solid',
  'outline',
  'soft',
  'subtle',
  'ghost'
] as const
function allowedVariant(
  variant: string | undefined
): (typeof allowedVariants)[number] | undefined {
  return allowedVariants.includes(variant as (typeof allowedVariants)[number])
    ? (variant as (typeof allowedVariants)[number])
    : undefined
}

// Only allow valid colors for UButton
const allowedColors = [
  'primary',
  'secondary',
  'success',
  'info',
  'warning',
  'error',
  'neutral'
] as const
function allowedColor(
  color: string | undefined
): (typeof allowedColors)[number] | undefined {
  return allowedColors.includes(color as (typeof allowedColors)[number])
    ? (color as (typeof allowedColors)[number])
    : undefined
}
</script>

<style scoped>
.jpm-sticky-cta {
  transform: translateY(12px);
  opacity: 0;
  transition:
    transform 220ms cubic-bezier(0.22, 0.9, 0.3, 1),
    opacity 180ms ease;
  will-change: transform, opacity;
}
.jpm-sticky-cta--visible {
  transform: translateY(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .jpm-sticky-cta,
  .jpm-sticky-cta--visible {
    transition: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}
</style>
